import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { IProgram, AcceptedFileTypes } from "@pbm/programs/contracts";
import { Grid, InputAdornment } from "@material-ui/core";
import {
  ApiLoadingSpinner,
  ApiRequestError,
  Button,
  GraphQLValidationErrors,
  SelectField,
  SelectFieldOption,
  TextField,
  FileUploadField,
  CheckBoxField,
} from "@funcional/react-ui";
import { useRegistrationPolicies } from "@pbm/registration-policies/hooks";
import { IGraphQLError } from "@common/contracts";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  toDateTimeLocalFormat,
  formatCnpj,
  formatPercentage,
} from "@common/helpers";
import { useEffect } from "react";

const containerSpacing = 3;

const schema = yup.object().shape({
  code: yup
    .string()
    .required()
    .min(3, "o código deve ter pelo menos 3 letras")
    .matches(
      /^[A-Z0-9_]*$/,
      "deve conter somente letras maiúsculas, números e sublinhado."
    ),
  establishmentIdentity: yup.string().required(),
  description: yup.string().required(),
  defaultTax: yup.string().required(),
  name: yup.string().required().min(3, "o nome deve ter pelo menos 3 letras"),
  type: yup.string().required().oneOf(["RETAIL", "INDUSTRY"]),
  effectiveSince: yup.date().required(),
  effectiveUntil: yup
    .date()
    .min(
      yup.ref("effectiveSince"),
      "a data final não pode ser inferior à inicial"
    )
    .required(),
});

export default function ProgramForm({
  onSubmit,
  defaults,
  isLoading,
  graphQLErrors,
}: {
  onSubmit: SubmitHandler<FieldValues>;
  defaults?: IProgram;
  isLoading?: boolean;
  graphQLErrors?: IGraphQLError[];
}) {
  const form = useForm({
    resolver: yupResolver(schema),
  });
  const {
    status: registrationPoliciesStatus,
    data: registrationPoliciesData,
    error: registrationPoliciesError,
  } = useRegistrationPolicies();
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = form;
  const registrationPolicyDefaultValue =
    defaults?.registrationPolicyId || defaults?.registrationPolicy?.id || "";

  useEffect(() => {
    if (typeof registrationPolicyDefaultValue === "string")
      setValue("registrationPolicyId", registrationPolicyDefaultValue);
  }, [registrationPoliciesData, registrationPolicyDefaultValue, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full max-w-xl">
      <GraphQLValidationErrors graphQLErrors={graphQLErrors} />
      <Grid container spacing={containerSpacing}>
        <Grid item xs={6}>
          <TextField
            required
            name="code"
            label="Código"
            control={control}
            defaultValue={defaults?.code}
            errorMessage={errors?.code?.message}
            helperText="Identificador alfanumérico único"
            readOnly={defaults && defaults.code ? true : false}
            transform={{
              input: (value: any) =>
                value ? value.toUpperCase().replace(/[^A-Z0-9_]/g, "") : value,
              output: (e: any) =>
                e.target.value
                  ? e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "")
                  : e.target.value,
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            required
            name="establishmentIdentity"
            label="CNPJ da empresa"
            control={control}
            defaultValue={defaults?.establishmentIdentity || ""}
            errorMessage={errors?.establishmentIdentity?.message}
            readOnly={defaults && defaults.establishmentIdentity ? true : false}
            transform={{
              input: (value: any) => (value ? formatCnpj(value) : value),
              output: (e: any) =>
                e.target.value ? formatCnpj(e.target.value) : e.target.value,
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <SelectField
            required
            control={control}
            defaultValue={defaults?.type || ""}
            errorMessage={errors?.type?.message}
            name="type"
            id="type"
            label="Tipo de programa"
          >
            <SelectFieldOption value="RETAIL">Rede</SelectFieldOption>
            <SelectFieldOption value="INDUSTRY">Indústria</SelectFieldOption>
          </SelectField>
        </Grid>

        <Grid item xs={12}>
          <CheckBoxField
            control={control}
            name="enableAdhesion"
            label="Habilitar Banner para Adesão"
            defaultChecked={defaults?.enableAdhesion || false}
            errorMessage={errors?.enableAdhesion?.message}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            required
            name="name"
            label="Nome"
            control={control}
            defaultValue={defaults?.name}
            errorMessage={errors?.name?.message}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            required
            name="description"
            label="Descrição"
            multiline
            rows={4}
            rowsMax={6}
            control={control}
            defaultValue={defaults?.description}
            errorMessage={errors?.description?.message}
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            required
            control={control}
            type="datetime-local"
            id="effectiveSince"
            label="Início da vigência"
            defaultValue={toDateTimeLocalFormat(defaults?.effectiveSince)}
            errorMessage={errors?.effectiveSince?.message}
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            required
            control={control}
            type="datetime-local"
            id="effectiveUntil"
            label="Fim da vigência"
            defaultValue={toDateTimeLocalFormat(defaults?.effectiveUntil)}
            errorMessage={errors?.effectiveUntil?.message}
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            required
            control={control}
            id="defaultTax"
            label="Taxa padrão"
            defaultValue={defaults?.defaultTax.toFixed(2) || 0}
            errorMessage={errors?.defaultTax?.message}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            transform={{
              input: (value: any) => (value ? formatPercentage(value) : value),
              output: (e: any) =>
                e.target.value
                  ? formatPercentage(e.target.value)
                  : e.target.value,
            }}
          />
        </Grid>

        <Grid item xs={12}>
          {registrationPoliciesStatus === "loading" ? (
            <ApiLoadingSpinner />
          ) : registrationPoliciesStatus === "error" ? (
            <ApiRequestError error={registrationPoliciesError} />
          ) : (
            <SelectField
              control={control}
              defaultValue={
                defaults?.registrationPolicyId ||
                defaults?.registrationPolicy?.id ||
                ""
              }
              errorMessage={errors?.registrationPolicyId?.message}
              name="registrationPolicyId"
              id="registrationPolicyId"
              label="Política cadastral"
            >
              <SelectFieldOption value={undefined}>-</SelectFieldOption>
              {registrationPoliciesData?.edges.map((item) => (
                <SelectFieldOption key={item.node.id} value={item.node.id}>
                  {item.node.description}
                </SelectFieldOption>
              ))}
            </SelectField>
          )}
        </Grid>

        <Grid item xs={6}>
          <TextField
            control={control}
            type="datetime-local"
            id="registrationPeriodStart"
            label="Data início para cadastro de CPF"
            defaultValue={toDateTimeLocalFormat(
              defaults?.registrationPeriodStart || undefined
            )}
            errorMessage={errors?.registrationPeriodStart?.message}
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            control={control}
            type="datetime-local"
            id="registrationPeriodEnd"
            label="Data limite para cadastro de CPF"
            defaultValue={toDateTimeLocalFormat(
              defaults?.registrationPeriodEnd || undefined
            )}
            errorMessage={errors?.registrationPeriodEnd?.message}
          />
        </Grid>

        <Grid item xs={6}>
          <FileUploadField
            control={control}
            setValue={setValue}
            accept=".png, .jpeg, .jpg, .gif"
            maxFileSize={1}
            id="logo"
            label="Logo do programa"
            defaultValue={defaults?.logo || ""}
            errorMessage={errors?.logo?.message}
          />
        </Grid>

        <Grid item xs={6}>
          <FileUploadField
            control={control}
            setValue={setValue}
            accept=".png, .jpeg, .jpg, .gif"
            maxFileSize={1}
            id="banner"
            label="Banner"
            defaultValue={defaults?.banner || ""}
            errorMessage={errors?.banner?.message}
          />
        </Grid>

        <Grid item xs={6}>
          <FileUploadField
            control={control}
            setValue={setValue}
            accept=".png, .jpeg, .jpg, .gif"
            maxFileSize={1}
            id="emailImage"
            label="Imagem do E-mail"
            defaultValue={defaults?.emailImage || ""}
            errorMessage={errors?.emailImage?.message}
          />
        </Grid>

        <Grid item xs={6}>
          <FileUploadField
            control={control}
            setValue={setValue}
            accept="application/pdf"
            maxFileSize={20}
            filesType={AcceptedFileTypes.DOCS}
            id="comercialCondition"
            label="Condição Comercial"
            defaultValue={defaults?.comercialCondition || ""}
            errorMessage={errors?.comercialCondition?.message}
          />
        </Grid>

        <Grid item xs={12}>
          <Button color="primary" type="submit" disabled={isLoading}>
            Salvar
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}
